import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AudioUpload } from "@/components/AudioUpload";

const empty = { title: "", summary: "", content: "", image: "", audio_url: "", video_url: "", published: true };

export const CornerManager = ({ onChange }) => {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/corner").then((r) => setPosts(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openEditor = (post) => {
    setEditing(post);
    setForm(post ? { title: post.title, summary: post.summary, content: post.content, image: post.image || "", audio_url: post.audio_url || "", video_url: post.video_url || "", published: post.published } : empty);
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/corner/${editing.id}`, form);
      else await api.post("/admin/corner", form);
      toast.success("Post saved");
      setOpen(false); load(); onChange?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try { await api.delete(`/admin/corner/${id}`); toast.success("Post deleted"); load(); onChange?.(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-lg md:text-2xl text-foreground">Bridgett's Corner</h2>
        <Button size="sm" data-testid="add-corner-btn" className="rounded-full" onClick={() => openEditor(null)}>
          <Plus size={14} className="mr-1" /> New post
        </Button>
      </div>

      <div className="space-y-2" data-testid="admin-corner-list">
        {posts.map((p) => (
          <div key={p.id} className="silk-card rounded-lg px-4 py-3 flex items-center justify-between gap-3">
            <p className="text-sm text-foreground truncate">{p.title} {!p.published && <span className="text-xs text-muted-foreground">(draft)</span>}</p>
            <div className="flex items-center gap-3 shrink-0">
              <button data-testid={`edit-corner-${p.id}`} onClick={() => openEditor(p)} className="text-muted-foreground hover:text-foreground"><Pencil size={15} /></button>
              <button data-testid={`delete-corner-${p.id}`} onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input data-testid="corner-editor-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Input data-testid="corner-editor-summary" placeholder="One-line summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
            <Textarea data-testid="corner-editor-content" rows={10} placeholder="Write from the heart… (blank line between paragraphs)" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
            <div className="grid sm:grid-cols-2 gap-4">
              <Input placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
              <Input placeholder="Video embed URL" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-2">Audio (optional) — upload or record</p>
              <AudioUpload value={form.audio_url} onChange={(url) => setForm({ ...form, audio_url: url })} />
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input type="checkbox" data-testid="corner-editor-published" checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-current" />
              Published (uncheck to keep as draft)
            </label>
            <Button data-testid="corner-editor-save" onClick={save} disabled={busy || !form.title || !form.content} className="rounded-full px-6">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
